import React from "react";
import {
  FiCreditCard,
  FiEdit2,
  FiExternalLink,
  FiEye,
  FiFacebook,
  FiGlobe,
  FiImage,
  FiInstagram,
  FiMail,
  FiMapPin,
  FiPhone,
  FiShoppingBag,
  FiTruck,
} from "react-icons/fi";

import { toAbsolutePublicUrl } from "../../../../services/mediaLibraryServices";

import AdminButton from "../../_shared/ui/AdminButton";
import AdminDrawer from "../../_shared/ui/AdminDrawer";
import CommerceStatusBadge from "./CommerceStatusBadge";

import {
  getCommerceAddress,
  getCommerceTags,
  getGoogleMapsUrl,
  normalizeExternalUrl,
} from "../utils/commercesAdminUtils";

export default function CommercesPreviewDrawer({
  open,
  item,
  onClose,
  onEditFromPreview,
}) {
  const logoUrl = item?.logo_url ? toAbsolutePublicUrl(item.logo_url) : "";
  const photoUrl = item?.photo_url ? toAbsolutePublicUrl(item.photo_url) : "";
  const mapsUrl = getGoogleMapsUrl(item);
  const address = getCommerceAddress(item);
  const tags = getCommerceTags(item?.tags);

  return (
    <AdminDrawer
      open={open}
      onClose={onClose}
      title="Aperçu du commerce"
      subtitle="Visualisez rapidement la fiche commerce."
      icon={FiEye}
      size="xl"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <AdminButton variant="secondary" onClick={onClose}>
            Fermer
          </AdminButton>

          {item?.id ? (
            <AdminButton
              icon={FiEdit2}
              variant="primary"
              onClick={onEditFromPreview}
            >
              Modifier ce commerce
            </AdminButton>
          ) : null}
        </div>
      }
    >
      {!item ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Commerce introuvable.
        </p>
      ) : (
        <article className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="relative min-h-[240px] bg-slate-100 dark:bg-slate-800">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={item.nom_commerce}
                className="h-[280px] w-full object-cover"
              />
            ) : (
              <div className="flex h-[240px] items-center justify-center bg-gradient-to-br from-orange-50 to-amber-100 text-orange-300 dark:from-slate-800 dark:to-slate-900 dark:text-slate-600">
                <FiShoppingBag className="text-6xl" />
              </div>
            )}

            <div className="absolute bottom-[-44px] left-6 flex h-28 w-28 items-center justify-center overflow-hidden rounded-[1.8rem] border-4 border-white bg-white p-4 shadow-xl dark:border-slate-900 dark:bg-slate-950">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={item.nom_commerce}
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <FiImage className="text-5xl text-slate-300 dark:text-slate-600" />
              )}
            </div>
          </div>

          <div className="px-5 pb-5 pt-16 sm:px-7 sm:pb-7">
            <div className="flex flex-wrap items-center gap-2">
              <CommerceStatusBadge active={!!item.is_active} />

              {item.categorie ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-bold text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                  <FiShoppingBag />
                  {item.categorie}
                </span>
              ) : null}

              {item.siret ? (
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                  SIRET : {item.siret}
                </span>
              ) : null}
            </div>

            <h1 className="mt-4 text-3xl font-bold leading-tight text-slate-950 dark:text-white">
              {item.nom_commerce}
            </h1>

            {item.contact_nom ? (
              <p className="mt-2 text-sm font-bold text-slate-500 dark:text-slate-400">
                {item.contact_nom}
                {item.contact_fonction ? ` — ${item.contact_fonction}` : ""}
              </p>
            ) : null}

            {tags.length ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          <div className="grid gap-5 border-t border-slate-200 p-5 dark:border-slate-800 sm:p-7 lg:grid-cols-[1fr_340px]">
            <div>
              {item.description_commerce ? (
                <div
                  className="prose prose-slate max-w-none dark:prose-invert prose-headings:font-bold prose-a:text-sky-600"
                  dangerouslySetInnerHTML={{
                    __html: item.description_commerce,
                  }}
                />
              ) : (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Aucune description renseignée.
                </p>
              )}
            </div>

            <aside className="space-y-4">
              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <p className="text-sm font-bold text-slate-950 dark:text-white">
                  Coordonnées
                </p>

                <div className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                  {item.tel ? (
                    <p className="flex items-center gap-2">
                      <FiPhone />
                      {item.tel}
                    </p>
                  ) : null}

                  {item.mail ? (
                    <p className="flex items-center gap-2">
                      <FiMail />
                      {item.mail}
                    </p>
                  ) : null}

                  {address ? (
                    <p className="flex items-start gap-2 whitespace-pre-line">
                      <FiMapPin className="mt-1 shrink-0" />
                      {address}
                    </p>
                  ) : null}

                  {item.gps_lat || item.gps_lng ? (
                    <p className="flex items-center gap-2">
                      <FiMapPin />
                      {item.gps_lat || "?"}, {item.gps_lng || "?"}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <p className="text-sm font-bold text-slate-950 dark:text-white">
                  Liens utiles
                </p>

                <div className="mt-3 flex flex-col gap-2">
                  {item.website ? (
                    <a
                      href={normalizeExternalUrl(item.website)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-2xl bg-sky-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-sky-700"
                    >
                      <FiGlobe />
                      Site web
                    </a>
                  ) : null}

                  {item.facebook ? (
                    <a
                      href={normalizeExternalUrl(item.facebook)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-700"
                    >
                      <FiFacebook />
                      Facebook
                    </a>
                  ) : null}

                  {item.instagram ? (
                    <a
                      href={normalizeExternalUrl(item.instagram)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-2xl bg-pink-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-pink-700"
                    >
                      <FiInstagram />
                      Instagram
                    </a>
                  ) : null}

                  {mapsUrl ? (
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-2xl bg-amber-500 px-4 py-3 text-sm font-bold text-white transition hover:bg-amber-600"
                    >
                      <FiExternalLink />
                      Ouvrir la carte
                    </a>
                  ) : null}

                  {!item.website && !item.facebook && !item.instagram && !mapsUrl ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Aucun lien renseigné.
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <p className="text-sm font-bold text-slate-950 dark:text-white">
                  Informations pratiques
                </p>

                <div className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                  {item.moyens_paiement ? (
                    <p className="flex items-start gap-2">
                      <FiCreditCard className="mt-1 shrink-0" />
                      {item.moyens_paiement}
                    </p>
                  ) : null}

                  {item.accessibilite ? (
                    <p>{item.accessibilite}</p>
                  ) : null}

                  {item.services ? (
                    <p className="flex items-start gap-2">
                      <FiTruck className="mt-1 shrink-0" />
                      {item.services}
                    </p>
                  ) : null}

                  {!item.moyens_paiement &&
                  !item.accessibilite &&
                  !item.services ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Aucune information pratique renseignée.
                    </p>
                  ) : null}
                </div>
              </div>
            </aside>
          </div>
        </article>
      )}
    </AdminDrawer>
  );
}